import React, { useState, useEffect } from 'react';
import { ExternalLink, Github } from 'lucide-react';
import styles from './Project.module.css';
import ImageModal from './ImageModal';

const Project = ({ project, index }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [landscapeImages, setLandscapeImages] = useState([]);
  const [portraitImages, setPortraitImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const isEven = index % 2 === 0;

  const openModal = (imageSrc) => {
    setModalImage(imageSrc);
    setModalOpen(true);
  };

  // Separate landscape and portrait images
  useEffect(() => {
    if (!project.images) return;

    const landscape = [];
    const portrait = [];

    project.images.forEach((imgSrc) => {
      const img = new Image();
      img.src = imgSrc;
      img.onload = () => {
        if (img.naturalWidth > img.naturalHeight) {
          landscape.push(imgSrc);
        } else {
          portrait.push(imgSrc);
        }
        setLandscapeImages([...landscape]);
        setPortraitImages([...portrait]);
      };
    });
  }, [project.images]);

  const mainImages = landscapeImages.length > 0 ? landscapeImages : project.images || [];

  return (
    <article className={`${styles.project} ${isEven ? styles.projectLeft : styles.projectRight}`}>
      {/* Project Images/Media */}
      <div className={styles.media}>
        {project.videoUrl ? (
          <div className={styles.videoWrapper}>
            <iframe
              src={project.videoUrl}
              title={`${project.title} demo`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className={styles.video}
            />
          </div>
        ) : (
          <>
            <div className={styles.imageContainer}>
              <img
                src={mainImages[selectedImage]}
                alt={`${project.title} screenshot ${selectedImage + 1}`}
                className={styles.mainImage}
                onClick={() => openModal(mainImages[selectedImage])}
                style={{ cursor: 'pointer' }}
              />

              {/* Mobile screenshots shown as overlay */}
              {portraitImages.length > 0 && (
                <div
                  className={styles.mobileOverlay}
                  onClick={() => openModal(portraitImages[0])}
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    src={portraitImages[0]}
                    alt={`${project.title} mobile view`}
                    className={styles.mobileImage}
                  />
                </div>
              )}
            </div>

            {mainImages.length > 1 && (
              <div className={styles.imageThumbnails}>
                {mainImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`${styles.thumbnail} ${selectedImage === idx ? styles.thumbnailActive : ''}`}
                    aria-label={`View screenshot ${idx + 1}`}
                  >
                    <img src={img} alt={`Thumbnail ${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Project Details */}
      <div className={styles.details}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>{project.title}</h2>
            <p className={styles.subtitle}>{project.subtitle}</p>
          </div>
          <div className={styles.links}>
            {project.liveUrl && (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
                aria-label="View live demo"
              >
                <ExternalLink size={18} />
                <span>Check it out</span>
              </a>
            )}
            {project.githubUrl && (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
                aria-label="View source code"
              >
                <Github size={18} />
                <span>Code</span>
              </a>
            )}
          </div>
        </div>

        {/* Tech Stack */}
        <div className={styles.techStack}>
          {project.tech.map((tech, idx) => (
            <span key={idx} className={styles.techBadge}>
              {tech}
            </span>
          ))}
        </div>

        {/* Description */}
        <p className={styles.description}>{project.description}</p>

        {/* Key Points */}
        {project.keyPoints && (
          <div className={styles.keyPoints}>
            <h3 className={styles.keyPointsTitle}>Key decisions:</h3>
            <ul className={styles.keyPointsList}>
              {project.keyPoints.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Current Challenge */}
        {project.currentChallenge && (
          <p className={styles.challenge}>{project.currentChallenge}</p>
        )}
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        imageSrc={modalImage}
        alt={project.title}
      />
    </article>
  );
};

export default Project;
